import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from 'antd';

import { AnnotationImageControls, Dashboard as DashboardBase, type Annotation, type DashboardRef } from '../features/annotation';
import withAnnotation from '../hoc/withAnnotation';
import useDamageMasters from '../hooks/useDamageMasters';
import { useMediaImageAnnotations } from '../hooks/useMediaImageAnnotations';
import { shouldCloseMediaAfterAnnotationCreate, shouldDownloadOriginalImage, shouldEnforceSingleAnnotation } from '../features/inspections/inspectionTabs';
import type { InspectionAsset } from '../types/common/asset';

const DashboardWithHandlers = withAnnotation(DashboardBase);

const cloneAnnotations = (source: Annotation[]) =>
  source.filter(annotation => annotation.deleted !== 1).map(annotation => ({ ...annotation }));

const hasUnsavedAnnotationChanges = (annotation: Annotation) => {
  // Ignore already updated/saved annotations
  if (annotation?.action === 'updated') {
    return false;
  }

  return annotation?.deleted !== 1 && (String(annotation?.id ?? '').includes('new_') || Boolean(annotation?.action));
};

export interface MediaImageItemData {
  id?: string;
  imageFileId?: string;
  imageURI: string;
  title: string;
  videoFrameTime?: number;
  raw?: InspectionAsset | Record<string, unknown>;
}

interface MediaImageItemProps {
  item: MediaImageItemData;
  inspectionId: string;
  annotations: Annotation[];
  setAnnotations: React.Dispatch<React.SetStateAction<Annotation[]>>;
  imageFrame: { width: number; height: number };
  onItemClick: (item: MediaImageItemData) => void;
  viewName: string;
  downloadFileName?: string;
  downloadAsset?: (url: string, fileName?: string) => Promise<void>;
  onAnnotationChanged?: () => Promise<void> | void;
  disableAnnotation?: boolean;
  activeTab?: string;
  onCreateSuccess?: () => void;
  onImageResolved?: (info: { imageFileId: string; imageURI?: string }) => void;
}

type AnnotationZoomControlProps = {
  zoomIn: () => void;
  zoomOut: () => void;
  resetTransform: () => void;
  [key: string]: unknown;
};

const MediaImageItem: React.FC<MediaImageItemProps> = ({
  item,
  inspectionId,
  annotations = [],
  setAnnotations,
  imageFrame,
  onItemClick,
  viewName,
  downloadFileName,
  downloadAsset,
  onAnnotationChanged,
  disableAnnotation = false,
  activeTab,
  onCreateSuccess,
  onImageResolved,
}) => {
  const childRef = useRef<DashboardRef | null>(null);
  const [selectedId, setSelectedId] = useState('');
  const [isAddAnnotation, setIsAddAnnotation] = useState(false);
  const [lastCommittedAnnotations, setLastCommittedAnnotations] = useState<Annotation[]>(() => cloneAnnotations(annotations));
  const { damageSeverity, damageTypes, damageArea } = useDamageMasters(inspectionId);

  const { saveAnnotation, deleteAnnotation } = useMediaImageAnnotations({
    inspectionId,
    item,
    viewName,
    annotations,
    setAnnotations,
    damageAreaOptions: damageArea?.items,
    damageTypeOptions: damageTypes?.items,
    damageSeverityOptions: damageSeverity?.items,
    onAnnotationChanged,
    onImageResolved,
  });
  const selectedAnnotation = useMemo(() => annotations.find(annotation => annotation.id === selectedId), [annotations, selectedId]);

  // Single-annotation guard is only for Defect Images. Vehicle Images / Defect Finder
  // allow drawing multiple new boxes, then bulk-save via /damages/multiple.
  const shouldGuardDefectImageActions = shouldEnforceSingleAnnotation(activeTab) && !disableAnnotation;
  const hasPendingDefectImageChanges = useMemo(
    () => shouldGuardDefectImageActions && annotations.some(hasUnsavedAnnotationChanges),
    [annotations, shouldGuardDefectImageActions]
  );

  useEffect(() => {
    if (!hasPendingDefectImageChanges) {
      setLastCommittedAnnotations(cloneAnnotations(annotations));
    }
  }, [annotations, hasPendingDefectImageChanges]);

  const discardUnsavedChanges = useCallback(
    (nextAction?: () => void) => {
      setAnnotations(cloneAnnotations(lastCommittedAnnotations));
      setSelectedId('');
      nextAction?.();
    },
    [lastCommittedAnnotations, setAnnotations]
  );

  const confirmDiscardUnsavedChanges = useCallback(
    (nextAction: () => void) => {
      if (!hasPendingDefectImageChanges) {
        nextAction();
        return;
      }

      Modal.confirm({
        title: 'Unsaved Changes',
        content: 'You have unsaved annotation changes. Select Discard to cancel your changes or Continue to keep editing.',
        okText: 'Discard',
        cancelText: 'Continue',
        okButtonProps: {
          style: {
            backgroundColor: '#eb4e2c',
            borderColor: '#eb4e2c',
          },
        },
        onOk: () => discardUnsavedChanges(nextAction),
      });
    },
    [discardUnsavedChanges, hasPendingDefectImageChanges]
  );

  const handleSelectAnnotation = useCallback(
    (nextId: string) => {
      if (nextId === selectedId) {
        return;
      }

      const nextAnnotation = annotations.find(annotation => annotation.id === nextId);

      if (shouldGuardDefectImageActions && nextId === '' && selectedAnnotation && hasUnsavedAnnotationChanges(selectedAnnotation)) {
        return;
      }

      const applySelection = () => {
        setSelectedId(nextId);
      };

      if (shouldGuardDefectImageActions && nextAnnotation && hasUnsavedAnnotationChanges(nextAnnotation)) {
        applySelection();
        return;
      }

      if (!shouldGuardDefectImageActions || nextId === '') {
        applySelection();
        return;
      }

      confirmDiscardUnsavedChanges(applySelection);
    },
    [annotations, confirmDiscardUnsavedChanges, selectedAnnotation, selectedId, shouldGuardDefectImageActions]
  );

  const handleStartAnnotationCreate = useCallback(
    (startCreate: () => void) => {
      // Vehicle Images / Defect Finder: keep existing pending boxes and start another.
      if (!shouldGuardDefectImageActions) {
        setSelectedId('');
        startCreate();
        return;
      }

      // Defect Images: only one in-progress annotation at a time.
      confirmDiscardUnsavedChanges(() => {
        setSelectedId('');
        startCreate();
      });
    },
    [confirmDiscardUnsavedChanges, shouldGuardDefectImageActions]
  );

  const handleToggleAnnotationMode = useCallback(() => {
    if (disableAnnotation) {
      return;
    }

    if (isAddAnnotation) {
      const disableAnnotationMode = () => {
        setIsAddAnnotation(false);
        setSelectedId('');
      };

      if (shouldGuardDefectImageActions) {
        confirmDiscardUnsavedChanges(disableAnnotationMode);
        return;
      }

      disableAnnotationMode();
      return;
    }

    setIsAddAnnotation(true);
  }, [confirmDiscardUnsavedChanges, disableAnnotation, isAddAnnotation, shouldGuardDefectImageActions]);

  const handleUploadDamages = useCallback((_metaJson: unknown, _id: unknown, _base64String: string, _width: number, _height: number) => {
    // Bulk upload path is not implemented for this media viewer yet.
  }, []);

  const handleDownloadAnnotations = useCallback(() => {
    // Vehicle Images: save the original file bytes (no canvas re-encode).
    // Defect Images: export the annotated portal canvas.
    if (shouldDownloadOriginalImage(activeTab)) {
      if (!item.imageURI || !downloadAsset) {
        return;
      }

      void downloadAsset(item.imageURI, downloadFileName);
      return;
    }

    childRef.current?.triggerSubmitMethod();
  }, [activeTab, downloadAsset, downloadFileName, item.imageURI]);

  const handleSaveCanvas = useCallback(async () => {
    const pendingAnnotation = annotations.find(
      annotation =>
        String(annotation?.id ?? '').includes('new_') &&
        annotation?.deleted !== 1 &&
        Boolean(annotation?.type) &&
        annotation?.type !== '00000'
    );

    if (!pendingAnnotation) {
      return false;
    }

    const result = await saveAnnotation(pendingAnnotation);
    setSelectedId('');

    if (result === 'created' && shouldCloseMediaAfterAnnotationCreate(activeTab)) {
      onCreateSuccess?.();
    }

    return result !== 'noop';
  }, [activeTab, annotations, onCreateSuccess, saveAnnotation]);

  const handleDamageCodeChange = useCallback((value: string) => {
    childRef.current?.triggerDamageMethod(value);
  }, []);

  const handleSaveAnnotation = useCallback(
    async (annotation: Annotation) => {
      await saveAnnotation(annotation);

      setAnnotations(previousAnnotations =>
        previousAnnotations.map(item =>
          item.id === annotation.id
            ? {
                ...item,
                action: undefined,
              }
            : item
        )
      );

      setSelectedId('');
    },
    [saveAnnotation, setAnnotations]
  );

  const handleDeleteAnnotation = useCallback(
    async (annotationId: string) => {
      await deleteAnnotation(annotationId);
      setSelectedId('');
    },
    [deleteAnnotation]
  );

  const handleClearAnnotation = useCallback(
    (annotationId: string) => {
      setAnnotations(previousAnnotations =>
        previousAnnotations.map(annotation => (annotation.id === annotationId ? { ...annotation, deleted: 1 } : annotation))
      );
      setSelectedId('');
    },
    [setAnnotations]
  );

  return (
    <div className="flex h-full w-full items-center justify-center" onClick={() => onItemClick(item)}>
      <DashboardWithHandlers
        ref={childRef}
        alt={item.title}
        image1={item.imageURI}
        imageFrame={imageFrame}
        cameraId={item.id ?? ''}
        downloadFileName={downloadFileName}
        annotations={annotations}
        imagesType={item.id ? 'damages' : 'frames'}
        setAnnotations={setAnnotations}
        selectedId={selectedId}
        selectAnnotation={handleSelectAnnotation}
        onStartAnnotationCreate={handleStartAnnotationCreate}
        isAddAnnotation={isAddAnnotation}
        disableAnnotation={disableAnnotation}
        handleAddAnnotations={handleToggleAnnotationMode}
        handleSubmitAnnotations1={() => {}}
        handleUpoadDamages={handleUploadDamages}
        user={{}}
        item={item}
        zoomControls={(zoomProps: AnnotationZoomControlProps) => (
          <AnnotationImageControls
            {...zoomProps}
            isAddAnnotation={isAddAnnotation}
            onToggleAnnotation={handleToggleAnnotationMode}
            onDownload={handleDownloadAnnotations}
            onSaveCanvas={handleSaveCanvas}
            onDamageCodeChange={handleDamageCodeChange}
            onSaveAnnotation={handleSaveAnnotation}
            onDeleteAnnotation={handleDeleteAnnotation}
            onClearAnnotation={handleClearAnnotation}
            selectedId={selectedId}
            annotations={annotations}
            damageSeverity={damageSeverity}
            damageTypes={damageTypes}
            damageArea={damageArea}
            disableAnnotation={disableAnnotation}
            activeTab={activeTab}
          />
        )}
      />
    </div>
  );
};

export default MediaImageItem;
